import React from 'react';
import ReactDOM from 'react-dom/client';
import {
  createBrowserRouter,
  RouterProvider,
  Navigate,
} from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import './index.css';
import { AuthProvider } from './context/authContext';

// Create a client for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});
import {
  Login,
  NotFound,
  AdminWrapper,
  Dashboard,
  MinisterListPage,
  MinisterDetailPage,
  CredentialRequestList,
  FunctionCredentialRequestList,
  CredentialRequestDetail,
  ReportsLandingPage,
  MinistersListReportPage,
  MinisterRegistryReportPage,
  MinisterTransfersReportPage,
  MinisterDegreeChangesReportPage,
  InstitutionLandingPage,
  EventListPage,
  EventFormPage,
  EventDetailPage,
} from './pages';
import TerritoryLayout from './pages/sections/territories/TerritoryLayout';
import InformationTab from './pages/sections/territories/InformationTab';
import MinistersTab from './pages/sections/territories/MinistersTab';
import ChurchesTab from './pages/sections/territories/ChurchesTab';
import CredentialsTab from './pages/sections/territories/CredentialsTab';
import HistorialTab from './pages/sections/territories/HistorialTab';
import DirectorioNacionalLayout from './pages/sections/territories/DirectorioNacionalLayout';
import NacionalInformationTab from './pages/sections/territories/NacionalInformationTab';
import NacionalHistorialTab from './pages/sections/territories/NacionalHistorialTab';
import NacionalMembersTab from './pages/sections/territories/NacionalMembersTab';
import NacionalCredentialsTab from './pages/sections/territories/NacionalCredentialsTab';
import { TithesPage } from './features/tithes/pages/TithesPage';
import { SettingsPage } from './features/settings/pages/SettingsPage';
import { ReceiptsListPage } from './features/receipts/pages/ReceiptsListPage';
import { PublicReceiptPage } from './features/receipts/pages/PublicReceiptPage';
import { PublicRegistrationPage } from './features/events/public/pages/PublicRegistrationPage';
import { PublicRegistrationViewPage } from './features/events/public/pages/PublicRegistrationViewPage';
import { PublicRegistrationEditPage } from './features/events/public/pages/PublicRegistrationEditPage';
import { OpenEventsPage } from './features/events/public/pages/OpenEventsPage';
import { isRegistrationHost } from './features/events/public/publicUrls';
import { EventCheckinPage } from './features/events/checkin/pages/EventCheckinPage';
import { KioskRedirect } from './features/events/checkin/pages/KioskRedirect';
import { RegistrationDeskPage } from './features/events/desk/pages/RegistrationDeskPage';
import { VotingListPage, ElectionDetailPage, KioskVotePage, PublicVotePage, ProjectionPage, ActaPage } from './features/voting';
import { Toaster } from '@/components/ui/toaster';
import './test-supabase';

// inscripcion.addbolivia.org serves only the public registration pages, with short paths
const registrationHostRouter = () =>
  createBrowserRouter([
    { path: '/', element: <OpenEventsPage />, errorElement: <NotFound /> },
    { path: '/p/:token', element: <PublicRegistrationViewPage />, errorElement: <NotFound /> },
    { path: '/editar/:token', element: <PublicRegistrationEditPage />, errorElement: <NotFound /> },
    { path: '/r/:token', element: <PublicReceiptPage />, errorElement: <NotFound /> },
    { path: '/v/:electionId', element: <PublicVotePage />, errorElement: <NotFound /> },
    { path: '/inscripcion/:code', element: <PublicRegistrationPage />, errorElement: <NotFound /> },
    { path: '/:code', element: <PublicRegistrationPage />, errorElement: <NotFound /> },
    { path: '*', element: <NotFound /> },
  ]);

const adminRouter = () => createBrowserRouter([
  {
    path: '/login',
    element: <Login />,
    errorElement: <NotFound />,
  },
  {
    // Public, unauthenticated — reached by scanning a receipt's QR code.
    path: '/r/:token',
    element: <PublicReceiptPage />,
    errorElement: <NotFound />,
  },
  {
    // Public, unauthenticated — events open for registration.
    path: '/inscripcion',
    element: <OpenEventsPage />,
    errorElement: <NotFound />,
  },
  {
    // Public, unauthenticated — event pre-registration form.
    path: '/inscripcion/:code',
    element: <PublicRegistrationPage />,
    errorElement: <NotFound />,
  },
  {
    // Public, unauthenticated — badge/receipt QR target: the filled registration form.
    path: '/p/:token',
    element: <PublicRegistrationViewPage />,
    errorElement: <NotFound />,
  },
  {
    // Public, unauthenticated — personal link an admin sends so a participant completes their form (ADB-169).
    path: '/editar/:token',
    element: <PublicRegistrationEditPage />,
    errorElement: <NotFound />,
  },
  {
    // Event-day check-in for ushers (auth required inside; no admin sidebar)
    path: '/checkin/:eventId',
    element: <EventCheckinPage />,
    errorElement: <NotFound />,
  },
  {
    // On-site registration desk: register, charge, receipt and credential (auth required inside)
    path: '/registro/:eventId',
    element: <RegistrationDeskPage />,
    errorElement: <NotFound />,
  },
  {
    // Public voting page, opened from the QR on the screen (no login)
    path: '/v/:electionId',
    element: <PublicVotePage />,
    errorElement: <NotFound />,
  },
  {
    // Big-screen projection of an election (auth + election.view checked inside; no sidebar)
    path: '/proyeccion/:electionId',
    element: <ProjectionPage />,
    errorElement: <NotFound />,
  },
  {
    // Printable results sheet
    path: '/acta/:electionId',
    element: <ActaPage />,
    errorElement: <NotFound />,
  },
  {
    // Voting kiosk: a volunteer votes on behalf of someone (auth + vote.proxy checked inside; no sidebar)
    path: '/votar/:electionId',
    element: <KioskVotePage />,
    errorElement: <NotFound />,
  },
  {
    path: '/kiosk/:eventId/:sessionId',
    element: <KioskRedirect />,
    errorElement: <NotFound />,
  },
  {
    path: '/',
    element: <AdminWrapper />,
    children: [
      {
        index: true,
        element: <Dashboard />,
      },
      {
        path: 'ministers',
        element: <MinisterListPage />,
      },
      {
        path: 'ministers/:id',
        element: <MinisterDetailPage />,
      },
      {
        path: 'credential-requests',
        element: <CredentialRequestList />,
      },
      {
        path: 'credential-requests/function',
        element: <FunctionCredentialRequestList />,
      },
      {
        path: 'credential-requests/function/:id',
        element: <CredentialRequestDetail />,
      },
      {
        path: 'credential-requests/:id',
        element: <CredentialRequestDetail />,
      },
      // Reports routes
      {
        path: 'reports',
        children: [
          {
            index: true,
            element: <ReportsLandingPage />,
          },
          {
            path: 'credentials/ministers-list',
            element: <MinistersListReportPage />,
          },
          {
            path: 'ministers/registry',
            element: <MinisterRegistryReportPage />,
          },
          {
            path: 'ministers/transfers',
            element: <MinisterTransfersReportPage />,
          },
          {
            path: 'ministers/degree-changes',
            element: <MinisterDegreeChangesReportPage />,
          },
        ],
      },
      // Institution routes
      {
        path: 'institutions/:slug',
        element: <InstitutionLandingPage />,
      },
      // Tithes
      {
        path: 'tithes',
        element: <TithesPage />,
      },
      // Receipts
      {
        path: 'receipts',
        element: <ReceiptsListPage />,
      },
      // Settings (admin)
      {
        path: 'settings',
        element: <SettingsPage />,
      },
      // Event routes
      {
        path: 'events',
        element: <EventListPage />,
      },
      {
        path: 'events/new',
        element: <EventFormPage />,
      },
      {
        path: 'events/:id',
        element: <EventDetailPage />,
      },
      {
        path: 'events/:id/edit',
        element: <EventFormPage />,
      },
      // Voting (access follows the election.* permissions)
      {
        path: 'voting',
        element: <VotingListPage />,
      },
      {
        path: 'voting/:electionId',
        element: <ElectionDetailPage />,
      },
      // Territory routes - Districts
      {
        path: 'territories/districts/:districtId',
        element: <TerritoryLayout />,
        children: [
          {
            index: true,
            element: <Navigate to="information" replace />,
          },
          {
            path: 'information',
            element: <InformationTab />,
          },
          {
            path: 'ministers',
            element: <MinistersTab />,
          },
          {
            path: 'churches',
            element: <ChurchesTab />,
          },
          {
            path: 'credentials',
            element: <CredentialsTab />,
          },
          {
            path: 'historial',
            element: <HistorialTab />,
          },
        ],
      },
      // Directorio Nacional
      {
        path: 'directorio-nacional',
        element: <DirectorioNacionalLayout />,
        children: [
          {
            index: true,
            element: <Navigate to="information" replace />,
          },
          {
            path: 'information',
            element: <NacionalInformationTab />,
          },
          {
            path: 'miembros',
            element: <NacionalMembersTab />,
          },
          {
            path: 'credenciales',
            element: <NacionalCredentialsTab />,
          },
          {
            path: 'historial',
            element: <NacionalHistorialTab />,
          },
        ],
      },
      // Territory routes - Regions
      {
        path: 'territories/regions/:regionId',
        element: <TerritoryLayout />,
        children: [
          {
            index: true,
            element: <Navigate to="information" replace />,
          },
          {
            path: 'information',
            element: <InformationTab />,
          },
          {
            path: 'ministers',
            element: <MinistersTab />,
          },
          {
            path: 'churches',
            element: <ChurchesTab />,
          },
          {
            path: 'credentials',
            element: <CredentialsTab />,
          },
          {
            path: 'historial',
            element: <HistorialTab />,
          },
        ],
      },
    ],
  },
]);

const router = isRegistrationHost() ? registrationHostRouter() : adminRouter();

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <RouterProvider router={router} />
        <Toaster />
      </AuthProvider>
    </QueryClientProvider>
  </React.StrictMode>,
);
